import React, { useState } from 'react';
import Modal from './ui/Modal';
import Button from './ui/Button';
import { icons } from './ui/Icons';

/**
 * Componente: WaitlistModal
 * Modal para entrar na fila de espera
 */
const WaitlistModal = ({ isOpen, onClose, onSubmit, barbershopName, selectedDate }) => {
  const [preferredTimes, setPreferredTimes] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Horários disponíveis para seleção
  const timeOptions = [
    '08:00', '08:30', '09:00', '09:30', '10:00', '10:30',
    '11:00', '11:30', '12:00', '12:30', '13:00', '13:30',
    '14:00', '14:30', '15:00', '15:30', '16:00', '16:30',
    '17:00', '17:30', '18:00', '18:30', '19:00', '19:30'
  ];

  const handleToggleTime = (time) => {
    setPreferredTimes(prev => {
      if (prev.includes(time)) {
        return prev.filter(t => t !== time);
      } else {
        return [...prev, time].sort();
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (preferredTimes.length === 0) {
      alert('Selecione pelo menos um horário preferido');
      return;
    }

    setIsSubmitting(true);
    
    try {
      await onSubmit({ preferredTimes });
      setPreferredTimes([]);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setPreferredTimes([]);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold mb-1">🔔 Entrar na Fila de Espera</h2>
              <p className="text-indigo-100 text-sm">{barbershopName}</p>
            </div>
            <button
              onClick={handleClose}
              className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
            >
              {icons.x({ className: 'h-6 w-6' })}
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Informações */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 mt-1">
                {icons.info({ className: 'h-5 w-5 text-blue-600' })}
              </div>
              <div className="text-sm text-blue-800">
                <p className="font-semibold mb-1">Como funciona?</p>
                <ul className="list-disc list-inside space-y-1 text-blue-700">
                  <li>Não há horários disponíveis para <strong>{new Date(selectedDate + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })}</strong></li>
                  <li>Selecione os horários que você prefere</li>
                  <li>Quando um horário for liberado, você será notificado</li>
                  <li>A notificação é por ordem de chegada na fila</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Seleção de Horários Preferidos */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Horários Preferidos ({preferredTimes.length} selecionados)
            </label>
            <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
              {timeOptions.map(time => {
                const isSelected = preferredTimes.includes(time);
                
                return (
                  <button
                    key={time}
                    type="button"
                    onClick={() => handleToggleTime(time)}
                    className={`
                      py-2 px-3 rounded-lg font-medium text-sm transition-all
                      ${isSelected
                        ? 'bg-indigo-600 text-white shadow-md scale-105'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }
                    `}
                  >
                    {time}
                  </button>
                );
              })}
            </div>
            <p className="mt-2 text-xs text-gray-500">
              💡 Dica: Selecione vários horários para aumentar suas chances
            </p>
          </div>

          {/* Ações */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <Button
              type="button"
              variant="secondary"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isSubmitting || preferredTimes.length === 0}
            >
              {isSubmitting ? (
                <>
                  {icons.spinner({ className: 'h-5 w-5 mr-2 animate-spin' })}
                  Entrando na Fila...
                </>
              ) : (
                <>
                  {icons.check({ className: 'h-5 w-5 mr-2' })}
                  Entrar na Fila
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default WaitlistModal;
